import { useEffect, useState } from "react";
import {
  DEFAULT_TIER,
  TIER_SPECS,
  artifactsForTier,
  tierBytes,
  type TierId,
  type VolumePhase,
} from "./volumes";

export type SetupPhase = "welcome" | "downloading" | "paused" | "verifying" | "ready" | "error";

export interface SetupStatus {
  phase: SetupPhase;
  bytes: number;
  totalBytes: number;
  bytesPerSecond?: number;
  chunksDone?: number;
  chunksTotal?: number;
  /** false면 판정 모델만 받는 단계(코퍼스는 아직 없음). 기본 true */
  showCorpus?: boolean;
  error?: string;
}

export type SetupVolumePhase = VolumePhase;

export interface SetupVolume {
  id: string;
  name: string;
  detail?: string;
  /** 이 볼륨의 전체 크기. 0이면 아직 크기 미정. */
  sizeBytes: number;
  /** 설치 후 실제 차지하는 바이트. 없으면 sizeBytes를 쓴다. */
  installedBytes?: number;
  phase: SetupVolumePhase;
  bytes?: number;
  bytesPerSecond?: number;
  etaSeconds?: number | null;
  error?: string;
  /** "file"이면 에어갭 가져오기 중. 일시정지 버튼을 숨긴다. */
  source?: "network" | "file";
  /** false면 파일에서 가져오기 버튼을 숨긴다. 기본 true */
  importable?: boolean;
}

export interface SetupPanelProps {
  onComplete: () => void;
  onSkip?: () => void;
  initialState?: SetupPhase;
  /** P0: 실제 네트워크 없이 진행률을 시뮬레이션한다. status가 있으면 무시. */
  mock?: boolean;
  /** 실제 다운로드 상태(controlled). 있으면 mock 시뮬레이션을 끈다. */
  status?: SetupStatus;
  /** 선택한 티어를 함께 넘긴다(기존 콜백은 인자를 무시해도 된다). */
  onStart?: (tier?: TierId) => void;
  /** 시작 버튼 문구. 로컬 가져오기 전용 화면에서는 "가져오기"로 바꾼다. */
  startLabel?: string;
  /** 티어 선택 UI를 보여줄지. 기본 true(코퍼스 화면). */
  showTiers?: boolean;
  /** 선택된 티어(controlled). 지정하면 내부 상태를 쓰지 않는다. */
  selectedTier?: TierId;
  /** 비제어 초기 티어. 기본값 cases. */
  defaultTier?: TierId;
  onSelectTier?: (tier: TierId) => void;
  onPause?: () => void;
  onResume?: () => void;
  onRetry?: () => void;
  /** 실제 코퍼스 볼륨 행. 없으면 기본 3행(법령/판례 색인/판례 전문)을 보여준다. */
  volumes?: SetupVolume[];
  onVolumePause?: (id: string) => void;
  onVolumeResume?: (id: string) => void;
  onVolumeCancel?: (id: string) => void;
  /** 에어갭: 선택한 파일을 볼륨 저장소로 넘긴다. */
  onVolumeImport?: (id: string, file: File) => void;
  /** 판정 모델은 이미 준비됐지만 코퍼스 볼륨만 남았을 때 모델 행을 완료로 표시한다. */
  modelReady?: boolean;
  /** false면 판정 모델 행을 숨긴다(로컬 전용 데모처럼 모델이 없는 화면). */
  showModel?: boolean;
  /** "나중에"로 설정을 건너뛸 수 있는지. 볼륨이 필수인 실제 빌드에서는 false. */
  allowSkip?: boolean;
}

const MODEL_MB = 299;
const STATUTE_BYTES = 75_161_600;
const CASE_INDEX_BYTES = 163_938_304;
const CASE_BM25_BYTES = 195_222_134;
const CASE_DOCPTR_BYTES = 3_172_093;

function formatMb(mb: number): string {
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)}GB` : `${Math.round(mb)}MB`;
}

function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || !Number.isFinite(bytes) || bytes <= 0) return "미정";
  return formatMb(bytes / 1e6);
}

function formatEta(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return "";
  }
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return minutes > 0 ? `${minutes}분 ${rest}초` : `${rest}초`;
}

const VOLUME_STATUS: Record<SetupVolumePhase, string> = {
  missing: "미설치",
  queued: "대기",
  downloading: "다운로드 중",
  paused: "일시정지",
  verifying: "확인 중",
  installed: "설치됨",
  error: "오류",
};

/** 로컬 파일 가져오기는 네트워크 다운로드가 아니다 — 문구를 분리한다. */
function volumeStatus(volume: SetupVolume): string {
  if (volume.phase === "downloading" && volume.source === "file") return "가져오는 중";
  return VOLUME_STATUS[volume.phase];
}

function volumeStateClass(phase: SetupVolumePhase): "waiting" | "active" | "done" | "error" {
  if (phase === "installed") return "done";
  if (phase === "downloading" || phase === "verifying") return "active";
  if (phase === "error") return "error";
  if (phase === "paused") return "waiting";
  return "waiting";
}

function volumeDetail(volume: SetupVolume): string {
  const percent =
    volume.sizeBytes > 0 && volume.bytes !== undefined
      ? Math.min(100, Math.round((volume.bytes / volume.sizeBytes) * 100))
      : null;
  switch (volume.phase) {
    case "installed":
      return `설치됨 · ${formatBytes(volume.installedBytes ?? volume.sizeBytes)}`;
    case "downloading": {
      if (volume.source === "file") {
        return percent !== null ? `${percent}% · 내 기기에서 복사 중` : "내 기기에서 복사 중";
      }
      const parts = [percent !== null ? `${percent}%` : "받는 중"];
      if (volume.bytesPerSecond) parts.push(`${(volume.bytesPerSecond / 1e6).toFixed(1)}MB/s`);
      const eta = formatEta(volume.etaSeconds);
      if (eta) parts.push(`${eta} 남음`);
      return parts.join(" · ");
    }
    case "verifying":
      return percent !== null ? `무결성 확인 ${percent}%` : "무결성 확인 중";
    case "paused":
      return percent !== null ? `일시정지 · ${percent}%` : "일시정지";
    case "error":
      return volume.error ?? (volume.source === "file" ? "가져오기 실패" : "다운로드 실패");
    case "queued":
      return "대기 중";
    default:
      return volume.detail ?? "미설치";
  }
}

function VolumeRow({
  volume,
  onPause,
  onResume,
  onCancel,
  onImport,
}: {
  volume: SetupVolume;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
  onImport?: (id: string, file: File) => void;
}) {
  const state = volumeStateClass(volume.phase);
  const canPause = volume.phase === "downloading" && volume.source !== "file";
  const canResume = volume.phase === "paused" || volume.phase === "error";
  const canCancel =
    volume.phase === "downloading" || volume.phase === "paused" || volume.phase === "verifying";
  const canImport = onImport !== undefined && volume.importable !== false;

  return (
    <li className={`setup-file setup-file-${state}`}>
      <span className="setup-file-icon setup-file-icon-corpus" aria-hidden="true">
        §
      </span>
      <span className="setup-file-main">
        <span className="setup-file-name">{volume.name}</span>
        <span className="setup-file-meta">{volumeDetail(volume)}</span>
        {canPause || canResume || canCancel || canImport ? (
          <span className="setup-volume-actions">
            {canPause && onPause ? (
              <button
                type="button"
                className="setup-volume-action"
                onClick={() => onPause(volume.id)}
              >
                일시정지
              </button>
            ) : null}
            {canResume && onResume ? (
              <button
                type="button"
                className="setup-volume-action"
                onClick={() => onResume(volume.id)}
              >
                {volume.phase === "error" ? "다시 시도" : "계속 받기"}
              </button>
            ) : null}
            {canCancel && onCancel ? (
              <button
                type="button"
                className="setup-volume-action"
                onClick={() => onCancel(volume.id)}
              >
                취소
              </button>
            ) : null}
            {canImport ? (
              <label className="setup-volume-action setup-volume-file">
                파일에서 가져오기
                <input
                  type="file"
                  accept=".aknpkg,.btpack,application/octet-stream"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    event.currentTarget.value = "";
                    if (file) onImport?.(volume.id, file);
                  }}
                />
              </label>
            ) : null}
          </span>
        ) : null}
      </span>
      <span className={`setup-file-status status-${state}`}>{volumeStatus(volume)}</span>
      <span className="setup-file-size">
        {volume.phase === "installed"
          ? formatBytes(volume.installedBytes ?? volume.sizeBytes)
          : formatBytes(volume.sizeBytes)}
      </span>
    </li>
  );
}

function FileRow({
  kind,
  name,
  detail,
  size,
  state,
}: {
  kind: "model" | "corpus";
  name: string;
  detail: string;
  size: string;
  state: "waiting" | "active" | "done";
}) {
  return (
    <li className={`setup-file setup-file-${state}`}>
      <span className={`setup-file-icon setup-file-icon-${kind}`} aria-hidden="true">
        {kind === "model" ? "◈" : "§"}
      </span>
      <span className="setup-file-main">
        <span className="setup-file-name">{name}</span>
        <span className="setup-file-meta">{detail}</span>
      </span>
      <span className={`setup-file-status status-${state}`}>
        {state === "done" ? "완료" : state === "active" ? "받는 중" : "대기"}
      </span>
      <span className="setup-file-size">{size}</span>
    </li>
  );
}

function defaultVolumes(phase: SetupPhase, modelDone: boolean): SetupVolume[] {
  const activePhase: SetupVolumePhase =
    phase === "ready"
      ? "installed"
      : phase === "paused"
        ? "paused"
        : phase === "verifying"
          ? "verifying"
          : phase === "downloading"
            ? "downloading"
            : phase === "error"
              ? "error"
              : "missing";
  const active = modelDone ? activePhase : "missing";
  const installed = phase === "ready";
  return [
    {
      id: "statute-core",
      name: "법령",
      detail: "민법·형법·민소 등 빈출",
      sizeBytes: STATUTE_BYTES,
      phase: installed ? "installed" : active,
      source: "network",
    },
    {
      id: "case-index",
      name: "판례 색인",
      detail: "대법원 판례 메타·인용",
      sizeBytes: CASE_INDEX_BYTES,
      phase: installed ? "installed" : "missing",
      source: "network",
    },
    {
      id: "case-bm25",
      name: "판례 검색 색인",
      detail: "BM25 키워드·관련 판례",
      sizeBytes: CASE_BM25_BYTES,
      phase: installed ? "installed" : "missing",
      source: "network",
      importable: true,
    },
    {
      id: "case-docptr",
      name: "판례 검색 포인터",
      detail: "BM25 문서 포인터",
      sizeBytes: CASE_DOCPTR_BYTES,
      phase: installed ? "installed" : "missing",
      source: "network",
      importable: true,
    },
    {
      id: "case-text",
      name: "판례 전문",
      detail: "판례 본문 · 선택",
      sizeBytes: 0,
      phase: "missing",
      source: "network",
      importable: true,
    },
  ];
}

export function SetupPanel({
  onComplete,
  onSkip,
  initialState = "welcome",
  mock = true,
  status,
  onStart,
  onPause,
  onResume,
  onRetry,
  volumes,
  onVolumePause,
  onVolumeResume,
  onVolumeCancel,
  onVolumeImport,
  modelReady,
  showModel = true,
  startLabel = "오프라인 데이터 받기",
  showTiers = true,
  selectedTier,
  defaultTier = DEFAULT_TIER,
  onSelectTier,
  allowSkip = true,
}: SetupPanelProps) {
  const controlled = status !== undefined;
  const [localPhase, setLocalPhase] = useState<SetupPhase>(initialState);
  const [progress, setProgress] = useState(initialState === "downloading" ? 0.28 : 0);
  const [quota, setQuota] = useState<number | null>(null);
  const [localTier, setLocalTier] = useState<TierId>(defaultTier);
  const tier = selectedTier ?? localTier;

  const selectTier = (next: TierId) => {
    if (selectedTier === undefined) setLocalTier(next);
    onSelectTier?.(next);
  };

  const phase = status?.phase ?? localPhase;

  useEffect(() => {
    if (controlled || !mock || phase !== "downloading") return;

    const id = window.setInterval(() => {
      setProgress((prev) => {
        const next = Math.min(1, prev + 0.012);
        if (next >= 1) setLocalPhase("verifying");
        return next;
      });
    }, 90);

    return () => window.clearInterval(id);
  }, [controlled, phase, mock]);

  useEffect(() => {
    if (controlled || phase !== "verifying") return;
    const id = window.setTimeout(() => setLocalPhase("ready"), 700);
    return () => window.clearTimeout(id);
  }, [controlled, phase]);

  useEffect(() => {
    if (!navigator.storage?.estimate) return;
    void navigator.storage
      .estimate()
      .then((estimate) => setQuota(estimate.quota ?? null))
      .catch(() => setQuota(null));
  }, []);

  const includeCorpus = status?.showCorpus !== false;
  const tierKeys = new Set<string>(artifactsForTier(tier));
  /** 티어에 속한 볼륨만 남긴다. 커스텀 볼륨만 넘어오면 그대로 보여준다. */
  const rowsForTier = (rows: SetupVolume[]): SetupVolume[] => {
    const matched = rows.filter((volume) => tierKeys.has(volume.id));
    return matched.length > 0 ? matched : rows;
  };
  const providedTierBytes = volumes
    ? rowsForTier(volumes).reduce((sum, volume) => sum + volume.sizeBytes, 0)
    : null;
  // 이미 설치된 모델(modelReady)은 받을 총량에서 뺀다.
  const modelBytes = showModel && !modelReady ? MODEL_MB * 1e6 : 0;
  const fallbackTotalBytes = includeCorpus
    ? modelBytes + (providedTierBytes ?? tierBytes(tier))
    : modelBytes;
  /** 웰컴 화면 총량은 선택한 티어를 즉시 반영한다(진행 중에는 실제 total을 쓴다). */
  const welcomeTotalBytes = includeCorpus
    ? modelBytes + (providedTierBytes ?? tierBytes(tier))
    : status?.totalBytes ?? modelBytes;
  const totalBytes =
    phase === "welcome" ? welcomeTotalBytes : status?.totalBytes ?? fallbackTotalBytes;
  const downloadedBytes = status?.bytes ?? totalBytes * progress;
  const downloadedMb = Math.round(downloadedBytes / 1e6);
  const totalMb = Math.round(totalBytes / 1e6);
  const ratio = totalBytes > 0 ? downloadedBytes / totalBytes : 0;
  const percent = Math.round(Math.min(1, ratio) * 100);
  const modelShare =
    showModel && includeCorpus && !modelReady ? MODEL_MB / Math.max(1, totalBytes / 1e6) : 1;
  const modelDone = modelReady ?? (status ? ratio >= modelShare : progress >= modelShare);
  const volumeRows = includeCorpus
    ? rowsForTier(volumes ?? defaultVolumes(phase, modelDone))
    : [];
  const modelSize = formatMb(includeCorpus ? MODEL_MB : totalBytes / 1e6);
  const allDone = phase === "ready";
  const paused = phase === "paused";
  const working = phase === "downloading" || phase === "verifying" || phase === "paused";
  const rateLabel = status?.bytesPerSecond
    ? `${(status.bytesPerSecond / 1e6).toFixed(1)}MB/s`
    : "12.4MB/s";
  /** 진행 중인 볼륨이 로컬 파일 가져오기면 네트워크 문구를 쓰지 않는다. */
  const fileMode = volumeRows.some(
    (volume) => volume.phase === "downloading" && volume.source === "file",
  );

  const start = () => {
    if (controlled) {
      onStart?.(tier);
      return;
    }
    setProgress(0.02);
    setLocalPhase("downloading");
  };

  const retry = () => {
    if (controlled) {
      onRetry?.();
      return;
    }
    setProgress(0);
    setLocalPhase("downloading");
  };

  const togglePause = () => {
    if (controlled) {
      if (paused) onResume?.();
      else onPause?.();
      return;
    }
    setLocalPhase(paused ? "downloading" : "paused");
  };

  const volumeList = (
    <ul className="setup-files">
      {showModel ? (
        <FileRow
          kind="model"
          name="법령 판정 모델"
          detail="기기 내에서 판정"
          size={modelSize}
          state={allDone ? "done" : modelDone ? "done" : working ? "active" : "waiting"}
        />
      ) : null}
      {volumeRows.map((volume) => (
        <VolumeRow
          key={volume.id}
          volume={volume}
          onPause={onVolumePause}
          onResume={onVolumeResume}
          onCancel={onVolumeCancel}
          onImport={onVolumeImport}
        />
      ))}
    </ul>
  );

  return (
    <div className="setup-shell">
      <main className="panel setup-panel">
        <header className="setup-top">
          <span className="brand">
            <span className="mark" aria-hidden="true">
              法
            </span>
            <span className="brand-name">법통</span>
            <span className="beta">BETA</span>
          </span>
          <span className="setup-step">{allDone ? "2 / 2" : "1 / 2"}</span>
        </header>

        <div className="setup-body">
          {phase === "error" ? (
            <>
              <p className="setup-kicker">설정 오류</p>
              <h1 className="setup-title">다운로드를 마치지 못했습니다.</h1>
              <p className="setup-lead">
                네트워크 상태를 확인한 뒤 다시 시도해 주세요. 이미 받은 데이터는 이어받습니다.
              </p>
              <div className="setup-error-box">
                <strong>{status?.error ?? "연결이 끊겼습니다"}</strong>
                <span>
                  판정 모델 {modelSize} · 청크 {status?.chunksDone ?? 4}/
                  {status?.chunksTotal ?? 5}
                </span>
              </div>
              {includeCorpus ? volumeList : null}
              <div className="setup-actions">
                <button type="button" className="setup-primary" onClick={retry}>
                  다시 시도
                </button>
                {allowSkip ? (
                  <button type="button" className="setup-secondary" onClick={onSkip ?? onComplete}>
                    나중에
                  </button>
                ) : null}
              </div>
            </>
          ) : allDone ? (
            <>
              <div className="setup-ready-icon" aria-hidden="true">
                ✓
              </div>
              <p className="setup-kicker">준비 완료</p>
              <h1 className="setup-title">이제 네트워크 없이 검색됩니다.</h1>
              <p className="setup-lead">
                {includeCorpus
                  ? "법령·판례와 판정 모델이 이 기기에 저장되었습니다. 질의와 문서는 어디에도 전송되지 않습니다."
                  : "판정 모델이 이 기기에 저장되었습니다. 질의와 문서는 어디에도 전송되지 않습니다."}
              </p>
              {includeCorpus ? volumeList : null}
              <div className="setup-actions">
                <button type="button" className="setup-primary" onClick={onComplete}>
                  검색 시작
                </button>
              </div>
            </>
          ) : phase === "welcome" ? (
            <>
              <p className="setup-kicker">오프라인 설정</p>
              <h1 className="setup-title">법통은 오프라인입니다.</h1>
              <p className="setup-lead">
                {includeCorpus
                  ? "최초 1회만 판정 모델과 법령·판례를 받습니다. 이후 검색과 인용·보류·제외 판정은 이 기기 안에서 끝납니다."
                  : "최초 1회만 판정 모델을 받습니다. 이후 검색과 인용·보류·제외 판정은 이 기기 안에서 끝납니다."}
              </p>
              {includeCorpus && showTiers ? (
                <>
                  <p className="setup-tier-heading">설치 범위</p>
                  <ul className="setup-tiers" role="radiogroup" aria-label="설치 범위">
                    {TIER_SPECS.map((spec) => {
                      const active = spec.id === tier;
                      return (
                        <li key={spec.id}>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={active}
                            className={`setup-tier${active ? " setup-tier-active" : ""}`}
                            onClick={() => selectTier(spec.id)}
                          >
                            <span className="setup-tier-main">
                              <span className="setup-tier-label">{spec.label}</span>
                              <span className="setup-tier-detail">{spec.description}</span>
                            </span>
                            <span className="setup-tier-size">
                              {formatBytes(tierBytes(spec.id))}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : null}
              {volumeList}
              <div className="setup-total">
                <span>총 {formatMb(totalBytes / 1e6)}</span>
                <span>
                  {quota ? `디스크 여유 ${formatMb(quota / 1e6)}` : "디스크 확인 중"}
                </span>
              </div>
              <div className="setup-actions">
                <button type="button" className="setup-primary" onClick={start}>
                  {startLabel}
                </button>
                {allowSkip ? (
                  <button type="button" className="setup-secondary" onClick={onSkip ?? onComplete}>
                    나중에
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <p className="setup-kicker">{fileMode ? "가져오기" : "다운로드"}</p>
              <h1 className="setup-title">
                {phase === "verifying"
                  ? "무결성을 확인하는 중…"
                  : fileMode
                    ? "내 기기에서 가져오는 중…"
                    : paused
                      ? "일시정지됨"
                      : "받는 중…"}
              </h1>
              <p className="setup-lead">
                {fileMode
                  ? "선택한 파일은 이 브라우저 안에서만 복사됩니다. 서버로 전송되지 않습니다."
                  : "패널을 닫아도 이어받을 수 있습니다. 받는 동안에는 검색을 잠시 사용할 수 없습니다."}
              </p>
              {volumeList}

              <div className="setup-progress">
                <div
                  className="setup-track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                >
                  <span className="setup-fill" style={{ width: `${percent}%` }} />
                </div>
                <div className="setup-stats">
                  <span>
                    {downloadedMb}MB / {totalMb}MB · {percent}%
                  </span>
                  <span>{fileMode ? "로컬 복사" : phase === "verifying" ? "무결성 확인" : rateLabel}</span>
                </div>
              </div>

              <div className="setup-actions">
                {fileMode ? null : (
                  <button
                    type="button"
                    className="setup-primary"
                    onClick={togglePause}
                    disabled={phase === "verifying"}
                  >
                    {paused ? "계속 받기" : "일시정지"}
                  </button>
                )}
                {allowSkip ? (
                  <button type="button" className="setup-secondary" onClick={onSkip ?? onComplete}>
                    나중에
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>

        <footer className="setup-foot">
          <span>검색 질의·문서는 전송되지 않습니다</span>
          <span>{fileMode ? "가져오는 중" : working ? "다운로드 중" : "로컬 저장"}</span>
        </footer>
      </main>
    </div>
  );
}
